/**
 * Which game-core events put text over the dungeon, and what it says
 * (integration plan slice 2b; ADR 0005: labels, badges and banners are DOM).
 * The shell asks this for every event as the renderer reaches it: a floating
 * label over a unit (damage and heal numbers, exp, level-ups, skills learned,
 * status names, stage chevrons, misses, the names of skills, items and
 * passives, skipped turns, charges, items lost) or a banner (floors, the
 * boss, the egg, the end of the run). Text keys are `ui.label.*`,
 * `ui.banner.*` and `ui.badge.*`; names are content ids the string table
 * names. Composition only: nothing here decides what happened. Every event of
 * a `labelledTypes` type makes exactly one label (the browser test counts
 * them), and every event of a `bannerTypes` type one banner.
 */
import { type Event, type RunState, type StageStat, stageTotal } from '@ab/game-core';
import { type FloorDirection, floorName } from '@ab/session';
import type { BadgeRow, LabelKind, TextArgs } from '@ab/ui';

export type LabelSpec = TextArgs & { readonly unit: string | null; readonly kind: LabelKind; readonly key: string };
export type BannerSpec = TextArgs & { readonly key: string; readonly tone: 'plain' | 'warn' };
export type Overlay = { readonly label: LabelSpec } | { readonly banner: BannerSpec } | null;

export const labelledTypes = [
  'damaged',
  'healed',
  'missed',
  'exp_gained',
  'level_up',
  'skill_learned',
  'status_applied',
  'status_cured',
  'status_expired',
  'stage_changed',
  'items_lost',
  'skill_used',
  'item_used',
  'passive_triggered',
  'action_skipped',
  'woke_up',
  'charge_started',
  'charge_fired',
  'charge_interrupted',
  'item_auto_used',
  'revived',
] as const satisfies readonly Event['type'][];

export const bannerTypes = ['floor_entered', 'floor_cleared', 'boss_defeated', 'egg_obtained', 'run_completed', 'run_failed', 'run_escaped'] as const satisfies readonly Event['type'][];

const label = (unit: string | null, kind: LabelKind, key: string, args: TextArgs = {}): Overlay => ({ label: { unit, kind, key, ...args } });
const banner = (key: string, args: TextArgs = {}, tone: BannerSpec['tone'] = 'plain'): Overlay => ({ banner: { key, tone, ...args } });

/**
 * What the shell knows while it walks a command's events: each unit's stage totals so far (seeded from the state
 * before), and which way the dungeon's floors go (the floor banners say B1F or 1F, ADR 0032).
 */
export type OverlayContext = { readonly stageBefore: (unit: string, stat: StageStat) => number; readonly direction: FloorDirection };

/** Stage totals seeded from a state and updated by each `stage_changed` (whose `stages` is the new total, not the change). */
export const stageTracker = (before: RunState, direction: FloorDirection): OverlayContext & { update(e: Event): void } => {
  const totals = new Map<string, number>();
  const key = (unit: string, stat: StageStat): string => `${unit}:${stat}`;
  const stageBefore = (unit: string, stat: StageStat): number => {
    const known = totals.get(key(unit, stat));
    if (known !== undefined) return known;
    const u = before.units.find((v) => v.id === unit);
    return u === undefined ? 0 : stageTotal(u.stages, stat);
  };
  return {
    stageBefore,
    direction,
    update(e) {
      if (e.type === 'stage_changed') totals.set(key(e.unit, e.stat), e.stages);
    },
  };
};

/** The label or banner an event shows, named against the states before and after its command. */
export const overlayFor = (e: Event, before: RunState, after: RunState, ctx: OverlayContext): Overlay => {
  switch (e.type) {
    case 'damaged':
      return label(e.unit, 'damage', 'ui.label.damage', { values: { amount: e.amount } });
    case 'healed':
      return e.amount > 0 ? label(e.unit, 'heal', 'ui.label.heal', { values: { amount: e.amount } }) : label(e.unit, 'heal', 'ui.label.heal_full');
    case 'missed':
      return label(e.target, 'miss', 'ui.label.miss');
    case 'exp_gained':
      return label(e.unit, 'exp', 'ui.label.exp', { values: { amount: e.amount } });
    case 'level_up':
      return label(e.unit, 'level', 'ui.label.level_up', { values: { level: e.level } });
    case 'skill_learned':
      return label(e.unit, 'level', 'ui.label.skill_learned', { names: { skill: e.skill } });
    case 'status_applied':
    case 'status_cured':
    case 'status_expired':
      return label(e.unit, 'status', `ui.label.${e.type}`, { keys: { status: `ui.badge.${e.status}` } });
    case 'stage_changed': {
      // `stages` is the new total: the label says which way it moved (a +2 buff dropping to +1 is "down").
      const change = e.stages - ctx.stageBefore(e.unit, e.stat);
      const key = e.stages === 0 ? 'ui.label.stage_reset' : change > 0 ? 'ui.label.stage_up' : 'ui.label.stage_down';
      return label(e.unit, 'stage', key, { keys: { stat: `ui.badge.stat_${e.stat}` } });
    }
    case 'items_lost':
      return e.items.length > 0 ? label(after.party[0] ?? null, 'lost', 'ui.label.items_lost', { names: { items: e.items.map((i) => i.item) } }) : label(after.party[0] ?? null, 'lost', 'ui.label.items_lost_none');
    case 'skill_used':
      return label(e.unit, 'skill', 'ui.label.name', { names: { name: e.skill } });
    case 'item_used':
      return label(e.unit, 'skill', 'ui.label.name', { names: { name: e.item } });
    case 'passive_triggered':
      return label(e.unit, 'status', 'ui.label.name', { names: { name: e.passive } });
    case 'action_skipped':
      return label(e.unit, 'note', `ui.label.skip_${e.reason}`);
    case 'woke_up':
      return label(e.unit, 'note', 'ui.label.woke');
    case 'charge_started':
      return label(e.unit, 'skill', 'ui.label.charge_started', { names: { skill: e.skill } });
    case 'charge_fired':
      return label(e.unit, 'skill', 'ui.label.charge_fired', { names: { skill: e.skill } });
    case 'charge_interrupted':
      return label(e.unit, 'note', 'ui.label.charge_interrupted');
    case 'item_auto_used':
      return label(e.unit, 'skill', 'ui.label.name', { names: { name: e.item } });
    case 'revived':
      return label(e.unit, 'heal', 'ui.label.revived', { values: { hp: e.hp } });
    case 'floor_entered':
      return banner('ui.banner.floor_entered', { keys: { floor: floorName(ctx.direction, e.floorNumber) } });
    case 'floor_cleared':
      return banner('ui.banner.floor_cleared', { keys: { floor: floorName(ctx.direction, e.floorNumber) } });
    case 'boss_defeated': {
      const boss = before.units.find((u) => u.id === e.unit) ?? after.units.find((u) => u.id === e.unit);
      return banner('ui.banner.boss_defeated', { names: { boss: boss?.monster ?? e.unit } });
    }
    case 'egg_obtained':
      return banner('ui.banner.egg_obtained', { names: { monster: e.monster } });
    case 'run_completed':
      return banner('ui.banner.run_completed');
    case 'run_failed':
      return banner('ui.banner.run_failed', {}, 'warn');
    case 'run_escaped':
      return banner('ui.banner.run_escaped');
    case 'unit_spawned':
    case 'turned':
    case 'moved':
    case 'attacked':
    case 'item_picked_up':
    case 'item_left':
    case 'ap_changed':
    case 'sp_changed':
    case 'spring_used':
    case 'unit_defeated':
    case 'escaped':
    case 'round_ended':
      // A gesture on the canvas and a line in the log; no overlay.
      return null;
  }
};

const stageStats: readonly StageStat[] = ['patk', 'pdef', 'aatk', 'adef', 'spd'];

/** The badge rows of a state: every unit on the floor with a status, a raised or lowered stage, or a charge. */
export const badgeRows = (state: RunState): BadgeRow[] =>
  state.units.flatMap((u) => {
    if (u.down) return [];
    const stages = stageStats.map((stat) => ({ stat, stages: stageTotal(u.stages, stat) })).filter((s) => s.stages !== 0);
    const statuses = u.statuses.map((s) => s.status);
    return statuses.length > 0 || stages.length > 0 || u.charging !== null ? [{ unit: u.id, statuses, stages, charging: u.charging !== null }] : [];
  });
